export type StoredMessage = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  ts: number;
};

export type StoredConversation = {
  id: string;
  messages: StoredMessage[];
  createdAt: number;
  updatedAt: number;
  title?: string;
  language?: string; // 对话时使用的语言设置
  personaId?: string; // 对话使用的 persona/vibe
};

export type DetectedEmotion = {
  emotion: string;
  percentage: number;
  color?: string;
};

export type DailySummary = {
  date: string; // ISO date string, e.g. 2025-01-07
  summary: string;
  generatedAt: number;
  title?: string;
  primaryEmotion?: { emoji: string; label: string };
  lumiHeard?: string;
  detectedEmotions?: DetectedEmotion[];
  detailGeneratedAt?: number;
  detailSourceUpdatedAt?: number;
};

import { getJWTToken, loadAuthUser } from './auth';
import { getUserStorageKey } from '../utils/storage';
import { toCanonicalPersonaId } from '../utils/persona';

const CONVERSATION_STORAGE_KEY = 't2s_conversations';
const SUMMARY_STORAGE_KEY = 't2s_daily_summaries';

export function loadDailySummaries(): Record<string, DailySummary> {
  const user = loadAuthUser();
  const storageKey = getUserStorageKey(SUMMARY_STORAGE_KEY, user?.id);
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed: DailySummary[] = JSON.parse(raw);
    if (!Array.isArray(parsed)) return {};
    return parsed.reduce<Record<string, DailySummary>>((acc, item) => {
      if (item?.date) acc[item.date] = item;
      return acc;
    }, {});
  } catch {
    return {};
  }
}

export function saveDailySummaries(record: Record<string, DailySummary>) {
  const user = loadAuthUser();
  const storageKey = getUserStorageKey(SUMMARY_STORAGE_KEY, user?.id);
  try {
    localStorage.setItem(storageKey, JSON.stringify(Object.values(record)));
  } catch {}
}


export function loadStoredConversations(): StoredConversation[] {
  const user = loadAuthUser();
  const storageKey = getUserStorageKey(CONVERSATION_STORAGE_KEY, user?.id);
  // Optional: trigger migration here too if needed, but unlikely
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed
        .filter(Boolean)
        .map((conv) => ({
          ...conv,
          personaId: toCanonicalPersonaId(conv?.personaId),
        }));
    }
    return [];
  } catch (error) {
    console.error('[journal] 读取对话记录失败:', error);
    return [];
  }
}

export function discardStoredConversation(id: string) {
  const user = loadAuthUser();
  const storageKey = getUserStorageKey(CONVERSATION_STORAGE_KEY, user?.id);
  try {
    const conversations = loadStoredConversations();
    const filtered = conversations.filter((c) => c.id !== id);
    localStorage.setItem(storageKey, JSON.stringify(filtered));
    console.log(`[journal] Discarded invalid conversation ${id} from localStorage.`);
  } catch (error) {
    console.error('[journal] 放弃对话记录失败:', error);
  }
}

// Helper to get local date key (YYYY-MM-DD)
// This ensures we group by the USER's local day, not UTC
export const getDateKey = (dateInput: Date | number | string): string => {
  const date = new Date(dateInput);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export function groupConversationsByDate(conversations: StoredConversation[]): Record<string, StoredConversation[]> {
  const grouped: Record<string, StoredConversation[]> = {};
  conversations.forEach((conv) => {
    // Use local date key for consistent grouping
    const dateKey = getDateKey(conv.updatedAt || conv.createdAt);
    if (!grouped[dateKey]) {
      grouped[dateKey] = [];
    }
    grouped[dateKey].push(conv);
  });
  return grouped;
}

/** Latest activity time for a conversation (for picking “last updated” session per day). */
export function getConversationLastTimestamp(conv: StoredConversation): number {
  let latest = conv.updatedAt || conv.createdAt || 0;
  conv.messages?.forEach((msg) => {
    if (msg?.ts && msg.ts > latest) latest = msg.ts;
  });
  return latest;
}

/** Monday 00:00:00 local time for the week that contains `date`. */
export function getWeekStartMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}


function buildSummaryPrompt(conversations: StoredConversation[]): string {
  const lines: string[] = [];
  const maxCharacters = 6000;

  conversations
    .slice()
    .sort((a, b) => a.createdAt - b.createdAt)
    .forEach((conv, index) => {
      const header = `[Session ${index + 1}]`;
      lines.push(header);
      conv.messages
        .slice()
        .sort((a, b) => a.ts - b.ts)
        .forEach((msg) => {
          const time = new Date(msg.ts).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' });
          const roleLabel = msg.role === 'user' ? 'User' : 'AI';
          const text = (msg.text || '').replace(/\s+/g, ' ').trim();
          lines.push(`${time} ${roleLabel}: ${text}`);
        });
      lines.push('');
    });

  let result = '';
  for (let i = 0; i < lines.length; i++) {
    const next = result ? `${result}\n${lines[i]}` : lines[i];
    if (next.length > maxCharacters) {
      break;
    }
    result = next;
  }

  return result;
}

import { getRealtimeVoiceServerUrl } from './realtimeVoice/config';


export type JournalDetail = {
  title: string;
  primaryEmotion: { emoji: string; label: string };
  lumiHeard: string;
  detectedEmotions: DetectedEmotion[];
};

export type JournalDetailRecord = {
  summary: JournalDetail | null;
  generatedAt?: number;
  sourceUpdatedAt?: number;
  stale?: boolean;
  invalid?: boolean;
  meaningful?: boolean;
  skippedReason?: string;
  personaId?: string;
};

export type JournalSummaryListItem = {
  conversationId: string;
  title: string;
  primaryEmotion: { emoji: string; label: string };
  lumiHeard: string;
  personaId?: string;
  generatedAt?: number;
  sourceUpdatedAt?: number;
};

export function getJournalApiBase(): string {
  const wsUrl = getRealtimeVoiceServerUrl();
  return wsUrl.replace('ws://', 'http://').replace('wss://', 'https://').split('/ws/')[0];
}

export function buildAuthHeaders(): Record<string, string> {
  const token = getJWTToken();
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

function normalizeDetailRecord(data: any): JournalDetailRecord {
  const rawSummary = data?.summary;
  const rawEmotions = Array.isArray(rawSummary?.detectedEmotions) ? rawSummary.detectedEmotions : [];
  const summary = rawSummary
    ? {
        title: rawSummary.title || '',
        primaryEmotion: {
          emoji: rawSummary.primaryEmotion?.emoji || '',
          label: rawSummary.primaryEmotion?.label || '',
        },
        lumiHeard: rawSummary.lumiHeard || '',
        detectedEmotions: rawEmotions.map((e: { emotion?: string; percentage?: number; color?: string }) => ({
          emotion: e.emotion || '',
          percentage: e.percentage || 0,
          color: e.color || undefined,
        })),
      }
    : null;

  return {
    summary,
    generatedAt: typeof data?.generatedAt === 'number' ? data.generatedAt : undefined,
    sourceUpdatedAt: typeof data?.sourceUpdatedAt === 'number' ? data.sourceUpdatedAt : undefined,
    stale: Boolean(data?.stale),
    invalid: Boolean(data?.invalid),
    meaningful: typeof data?.meaningful === 'boolean' ? data.meaningful : undefined,
    skippedReason: typeof data?.skippedReason === 'string' ? data.skippedReason : undefined,
    personaId: typeof data?.personaId === 'string' ? data.personaId : undefined,
  };
}

export async function fetchJournalDetail(
  conversationId: string,
  sourceUpdatedAt?: number,
): Promise<JournalDetailRecord | null> {
  const token = getJWTToken();
  if (!token) return null;

  const httpBase = getJournalApiBase();
  const params = sourceUpdatedAt ? `?source_updated_at=${encodeURIComponent(sourceUpdatedAt)}` : '';
  const apiUrl = `${httpBase}/api/journal/summary/${encodeURIComponent(conversationId)}${params}`;

  try {
    const res = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        ...buildAuthHeaders(),
      },
    });

    if (!res.ok) {
      if (res.status === 404) {
        return { summary: null, invalid: true };
      }
      const text = await res.text().catch(() => '');
      throw new Error(`Fetch detail failed: ${res.status} ${text}`);
    }

    const data = await res.json();
    return normalizeDetailRecord(data);
  } catch (e) {
    console.error(`[journal] Failed to fetch stored detail: ${e}`);
    return null;
  }
}

export async function saveJournalDetail(
  conversationId: string,
  detail: JournalDetail,
  generatedAt?: number,
  sourceUpdatedAt?: number,
): Promise<JournalDetailRecord | null> {
  const token = getJWTToken();
  if (!token) return null;

  const httpBase = getJournalApiBase();
  const apiUrl = `${httpBase}/api/journal/summary/${encodeURIComponent(conversationId)}`;

  try {
    const res = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...buildAuthHeaders(),
      },
      body: JSON.stringify({
        ...detail,
        generatedAt,
        sourceUpdatedAt,
      }),
    });

    if (!res.ok) {
      if (res.status === 404) {
        return { summary: null, invalid: true };
      }
      const text = await res.text().catch(() => '');
      throw new Error(`Save detail failed: ${res.status} ${text}`);
    }

    const data = await res.json();
    return normalizeDetailRecord(data);
  } catch (e) {
    console.error(`[journal] Failed to save stored detail: ${e}`);
    return null;
  }
}

export async function generateAndStoreJournalDetail(
  conversationId: string,
  date: string,
  conversations: StoredConversation[],
  sourceUpdatedAt?: number,
  force?: boolean,
): Promise<JournalDetailRecord | null> {
  const token = getJWTToken();
  if (!token) return null;

  const httpBase = getJournalApiBase();
  const apiUrl = `${httpBase}/api/journal/summary/${encodeURIComponent(conversationId)}/generate`;

  const preparedPrompt = buildSummaryPrompt(conversations);
  const language = conversations[0]?.language || 'en-US';

  try {
    const res = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...buildAuthHeaders(),
      },
      body: JSON.stringify({
        text: preparedPrompt,
        date,
        language,
        sourceUpdatedAt,
        force: Boolean(force),
      }),
    });

    if (!res.ok) {
      if (res.status === 404) {
        return { summary: null, invalid: true };
      }
      const text = await res.text().catch(() => '');
      throw new Error(`Generate detail failed: ${res.status} ${text}`);
    }

    const data = await res.json();
    return normalizeDetailRecord(data);
  } catch (e) {
    console.error(`[journal] Failed to generate stored detail: ${e}`);
    return null;
  }
}

export async function generateJournalDetail(
  date: string,
  conversations: StoredConversation[],
): Promise<JournalDetail | null> {
  const httpBase = getJournalApiBase();
  const apiUrl = `${httpBase}/api/journal/summarize-detail`;

  const preparedPrompt = buildSummaryPrompt(conversations);
  const language = conversations[0]?.language || 'en-US';

  try {
    const res = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        text: preparedPrompt,
        date,
        language,
      }),
    });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Detail generation failed: ${res.status} ${text}`);
    }

    const data = await res.json();
    const rawEmotions = Array.isArray(data.detectedEmotions) ? data.detectedEmotions : [];
    return {
      title: data.title || '',
      primaryEmotion: {
        emoji: data.primaryEmotion?.emoji || '',
        label: data.primaryEmotion?.label || '',
      },
      lumiHeard: data.lumiHeard || '',
      detectedEmotions: rawEmotions.map((e: { emotion?: string; percentage?: number; color?: string }) => ({
        emotion: e.emotion || '',
        percentage: e.percentage || 0,
        color: e.color || undefined,
      })),
    };
  } catch (e) {
    console.error(`[journal] Failed to generate detail: ${e}`);
    return null;
  }
}

export async function fetchJournalSummaryList(limit = 200): Promise<JournalSummaryListItem[]> {
  const token = getJWTToken();
  if (!token) return [];

  const httpBase = getJournalApiBase();
  const apiUrl = `${httpBase}/api/journal/summaries?limit=${encodeURIComponent(limit)}`;

  try {
    const res = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        ...buildAuthHeaders(),
      },
    });

    if (!res.ok) {
      if (res.status === 404) {
        // Older backend deployments may not expose /api/journal/summaries.
        // Caller should fall back to per-conversation detail fetches.
        return [];
      }
      const text = await res.text().catch(() => '');
      throw new Error(`List summaries failed: ${res.status} ${text}`);
    }

    const data = await res.json();
    if (!Array.isArray(data?.items)) return [];

    return data.items
      .filter((it: any) => it && typeof it.conversationId === 'string')
      .map((it: any) => ({
        conversationId: it.conversationId,
        title: it.title || '',
        primaryEmotion: {
          emoji: it.primaryEmotion?.emoji || '',
          label: it.primaryEmotion?.label || '',
        },
        lumiHeard: it.lumiHeard || '',
        personaId: it.personaId,
        generatedAt: typeof it.generatedAt === 'number' ? it.generatedAt : undefined,
        sourceUpdatedAt: typeof it.sourceUpdatedAt === 'number' ? it.sourceUpdatedAt : undefined,
      }));
  } catch (e) {
    console.error(`[journal] Failed to fetch summary list: ${e}`);
    return [];
  }
}

export async function fetchRecentConversations(limit: number = 5): Promise<StoredConversation[]> {
  const token = getJWTToken();
  if (!token) return [];

  const httpBase = getJournalApiBase();
  const apiUrl = `${httpBase}/api/journal/recent-conversations?limit=${limit}`;

  try {
    const response = await fetch(apiUrl, {
      headers: {
        ...buildAuthHeaders(),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch recent conversations: ${response.status}`);
    }

    const data = await response.json();
    return (data || []).map((conv: any) => ({
      id: conv.id,
      messages: conv.messages,
      createdAt: conv.createdAt,
      updatedAt: conv.updatedAt,
      title: conv.title,
      language: conv.language,
      personaId: toCanonicalPersonaId(conv.personaId),
    }));
  } catch (error) {
    console.error('[journal] fetchRecentConversations failed:', error);
    return [];
  }
}
